import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { LanguageProvider } from './lib/i18n';
import SwipeBack from './lib/swipeBack';
import ScrollManager from './lib/scrollManager';
import './App.css';
import QRBookingApp from './Booking';
import StaffDeviceGate from './components/StaffDeviceGate';
const PhoneSearchOrders = React.lazy(() => import('./Search'));
const StatusTable = React.lazy(() => import('./StatusTable'));
const StaffLogin = React.lazy(() => import('./staff/StaffLogin'));
const StaffAdmin = React.lazy(() => import('./staff/StaffAdmin'));
const MasterStation = React.lazy(() => import('./staff/MasterStation'));
// Massage booking (owner 2026-10-25): TV QR, public guest page, bar form, reception schedule
const MassageGuest = React.lazy(() => import('./massage/MassageGuest'));
const MassageTV = React.lazy(() => import('./massage/MassageTV'));
const MassageBar = React.lazy(() => import('./massage/MassageBar'));
const ReceptionMassage = React.lazy(() => import('./massage/ReceptionMassage'));
const ReceptionDesk = React.lazy(() => import('./staff/ReceptionDesk'));  // OPS-1 (replaces ReceptionEnqueue)
const CardMasterDashboardWoman = React.lazy(() => import('./PillingWoman'));
const CardMasterDashboardMan = React.lazy(() => import('./PillingMan'));
const StatusTableMan = React.lazy(() => import('./StatusTableMan'));
const MassageStatus = React.lazy(() => import('./MassageStatus'));
const UpcomingVisitWidget = React.lazy(() => import('./pages/widgets/UpcomingVisitWidget'));
const TodayStatusWidget = React.lazy(() => import('./pages/widgets/TodayStatusWidget'));
const RepeatLastVisitWidget = React.lazy(() => import('./pages/widgets/RepeatLastVisitWidget'));
const BookingFlow = React.lazy(() => import('./pages/booking/BookingFlow'));
const PaymentSuccess = React.lazy(() => import('./pages/booking/PaymentSuccess'));
const PaymentFailure = React.lazy(() => import('./pages/booking/PaymentFailure'));
const MyBookings = React.lazy(() => import('./pages/mybookings/MyBookings'));
const LoyaltyHub = React.lazy(() => import('./pages/loyalty/LoyaltyHub'));
const SubscriptionPaymentSuccess = React.lazy(() => import('./pages/loyalty/SubscriptionPaymentSuccess'));
const SubscriptionPaymentFailure = React.lazy(() => import('./pages/loyalty/SubscriptionPaymentFailure'));
const ProfileHub = React.lazy(() => import('./pages/profile/ProfileHub'));
const RecoveryRhythm = React.lazy(() => import('./pages/activity/RecoveryRhythm'));
const HomeHub = React.lazy(() => import('./pages/home/HomeHub'));
const ProgramsHub = React.lazy(() => import('./pages/programs/ProgramsHub'));
const ServicesHub = React.lazy(() => import('./pages/services/ServicesHub'));
const MasterDetail = React.lazy(() => import('./pages/masters/MasterDetail'));
const MenuHub = React.lazy(() => import('./pages/menu/MenuHub'));
const NewsHub = React.lazy(() => import('./pages/news/NewsHub'));
const ClubHub = React.lazy(() => import('./pages/club/ClubHub'));


// P3: recover from a stale-deployment chunk 404 by reloading ONCE (index is no-store -> fresh hashes).
class ChunkErrorBoundary extends React.Component {
  constructor(p){ super(p); this.state = { failed: false }; }
  static getDerivedStateFromError(err){
    const isChunk = err && (/ChunkLoadError/.test(err.name || '') || /Loading chunk/.test(err.message || ''));
    if (isChunk) {
      try {
        if (!sessionStorage.getItem('chunkReloaded')) {
          sessionStorage.setItem('chunkReloaded', '1');
          window.location.reload();
          return { failed: false };
        }
      } catch (_e) { /* ignore */ }
      return { failed: true };
    }
    throw err;
  }
  render(){ return this.state.failed
    ? <div style={{padding:'24px',textAlign:'center'}}>Обновите страницу.</div>
    : this.props.children; }
}
function RouteFallback(){
  return <div style={{minHeight:'60vh',display:'flex',alignItems:'center',justifyContent:'center',
    paddingTop:'env(safe-area-inset-top)'}} aria-busy="true" />;
}

// Separate Navigation component
function Navigation() {
  return (
    <nav style={{
      background: 'linear-gradient(to right, #9333ea, #ec4899)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 50
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 1rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '64px'
        }}>
          <div>
            <span style={{
              color: 'white',
              fontSize: '1.25rem',
              fontWeight: 'bold'
            }}>888 Баня</span>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <Link 
              to="/" 
              style={{
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                fontWeight: '500',
                textDecoration: 'none',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Меню
            </Link>
            <Link 
              to="/search" 
              style={{
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                fontWeight: '500',
                textDecoration: 'none',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Админ
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}

// Layout wrapper that conditionally shows navigation
function Layout({ children }) {
  const location = useLocation();
  
  // Define routes that should NOT show the navigation
  const routesWithoutNav = [
    '/pilling-woman',
    '/pilling-man',
    '/status-man',
    '/status-woman',
    '/widgets/upcoming-visit',
    '/widgets/today-status',
    '/widgets/repeat-last-visit',
    '/booking-flow',
    '/booking-payment/success',
    '/booking-payment/failure',
    '/subscription-payment/success',
    '/subscription-payment/failure',
    '/my-bookings',
    '/loyalty',
    '/profile',
    '/activity',
    '/home',
    '/programs',
    '/services',
    '/menu',
    '/news',
    '/club',
    '/massage',
    '/massage-tv',
    '/bar/massage',
    '/reception/massage'
  ];

  const showNav = !routesWithoutNav.includes(location.pathname)
    && !location.pathname.startsWith('/programs/')
    && !location.pathname.startsWith('/services/')
    && !location.pathname.startsWith('/masters/')
    && !location.pathname.startsWith('/news/');
  
  return (
    <div className="min-h-screen">
      {showNav && <Navigation />}
      {children}
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <LanguageProvider>
    <BrowserRouter>
      <ScrollManager />
      <SwipeBack>
      <Layout>
        <ChunkErrorBoundary>
        <React.Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public QR-guest ordering screen — NOT staff, no device token. */}
          <Route path="/" element={<QRBookingApp />} />
          {/* RECEPTION: reception REST endpoints are StaffDevice-gated. */}
          <Route path="/search" element={<StaffDeviceGate role="RECEPTION"><PhoneSearchOrders /></StaffDeviceGate>} />
          {/* DISPLAY boards read the public card WebSocket (no REST auth); WS auth is a separate backend gap. */}
          <Route path="/table" element={<StatusTable />} />
          {/* MASTER_TABLET: card status mutation (update_card) is StaffDevice-gated. */}
          <Route path="/pilling-woman" element={<StaffDeviceGate role="MASTER_TABLET"><CardMasterDashboardWoman/></StaffDeviceGate>} />
          <Route path="/pilling-man" element={<StaffDeviceGate role="MASTER_TABLET"><CardMasterDashboardMan/></StaffDeviceGate>} />
          {/* TVs were physically swapped: /status-man shows the WOMEN's board and
              /status-woman shows the MEN's board, to match the screens' default URLs. */}
          <Route path="/status-man" element={<StatusTable/>} />
          <Route path="/status-woman" element={<StatusTableMan/>} />
          {/* Staff Access OS (new auth; parallel to legacy StaffDeviceGate) */}
          <Route path="/staff" element={<StaffLogin/>} />
          <Route path="/staff/admin" element={<StaffAdmin/>} />
          <Route path="/staff/station" element={<MasterStation/>} />
          <Route path="/reception" element={<ReceptionDesk/>} />
          <Route path="/reception/massage" element={<ReceptionMassage/>} />
          <Route path="/bar/massage" element={<MassageBar/>} />
          <Route path="/massage" element={<MassageGuest/>} />
          <Route path="/massage-tv" element={<MassageTV/>} />
          <Route path="/massage-status" element={<StaffDeviceGate role="MASTER_TABLET"><MassageStatus/></StaffDeviceGate>} />
          <Route path="/widgets/upcoming-visit" element={<UpcomingVisitWidget />} />
          <Route path="/widgets/today-status" element={<TodayStatusWidget />} />
          <Route path="/widgets/repeat-last-visit" element={<RepeatLastVisitWidget />} />
          <Route path="/booking-flow" element={<BookingFlow />} />
          <Route path="/booking-payment/success" element={<PaymentSuccess />} />
          <Route path="/booking-payment/failure" element={<PaymentFailure />} />
          <Route path="/menu" element={<MenuHub />} />
          <Route path="/club" element={<ClubHub />} />
          <Route path="/news" element={<NewsHub />} />
          <Route path="/news/:id" element={<NewsHub />} />
          <Route path="/my-bookings" element={<MyBookings />} />
          <Route path="/loyalty" element={<LoyaltyHub />} />
          <Route path="/subscription-payment/success" element={<SubscriptionPaymentSuccess />} />
          <Route path="/subscription-payment/failure" element={<SubscriptionPaymentFailure />} />
          <Route path="/profile" element={<ProfileHub />} />
          <Route path="/activity" element={<RecoveryRhythm />} />
          <Route path="/home" element={<HomeHub />} />
          <Route path="/programs" element={<ProgramsHub />} />
          <Route path="/programs/:id" element={<ProgramsHub />} />
          <Route path="/services" element={<ServicesHub />} />
          <Route path="/services/:id" element={<ServicesHub />} />
          <Route path="/masters/:id" element={<MasterDetail />} />

          <Route 
            path="*" 
            element={
              <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(to bottom right, #fee2e2, #fed7aa)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '1.5rem',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                  padding: '2rem',
                  textAlign: 'center'
                }}>
                  <h1 style={{
                    fontSize: '2.25rem',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    marginBottom: '1rem'
                  }}>404</h1>
                  <p style={{
                    color: '#6b7280',
                    marginBottom: '1.5rem'
                  }}>Страница не найдена</p>
                  <Link 
                    to="/" 
                    style={{
                      background: 'linear-gradient(to right, #9333ea, #ec4899)',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                      textDecoration: 'none',
                      display: 'inline-block',
                      transition: 'all 0.3s'
                    }}
                  >
                    Вернуться на главную
                  </Link>
                </div>
              </div>
            } 
          />
        </Routes>
        </React.Suspense>
        </ChunkErrorBoundary>
      </Layout>
      </SwipeBack>
    </BrowserRouter>
    </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;